import type React from 'react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import styles from './avatar.module.scss';

export interface AvatarProps {
  name: string;
  src?: string;
  color?: string;
  size?: number;
  online?: boolean;
  emblem?: React.ReactNode;
  className?: string;
}

const EMOJI_RE = /\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu;

const BG_TOKENS = ['--wh-coral', '--wh-plum', '--wh-sun', '--wh-teal'] as const;

export function deriveInitials(name: string): string {
  const stripped = name.replace(EMOJI_RE, '').trim();
  const words = stripped.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = [...words[0]][0] ?? '';
  if (words.length === 1) return first.toUpperCase();
  const last = [...words[words.length - 1]][0] ?? '';
  return (first + last).toUpperCase();
}

export function deriveColor(name: string): string {
  let h = 0;
  for (const ch of name) {
    h = (Math.imul(h, 31) + (ch.codePointAt(0) ?? 0)) | 0;
  }
  return BG_TOKENS[Math.abs(h) % BG_TOKENS.length];
}

const FONT_SIZES: Record<number, number> = {
  28: 11,
  36: 13,
  48: 16,
  64: 21,
};

export const Avatar: React.FC<AvatarProps> = ({ name, src, color, size = 48, online = false, emblem, className }) => {
  const [hasError, setHasError] = useState(false);

  const bgToken = color ?? deriveColor(name);
  const initials = deriveInitials(name);
  const showImage = Boolean(src) && !hasError && !emblem;
  const fontSize = FONT_SIZES[size] ?? Math.round(size * 0.33);

  const ariaLabel = online ? `${name}'s avatar, online` : `${name}'s avatar`;

  const cls = [styles.avatar, className].filter(Boolean).join(' ');

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={cls}
      style={
        {
          '--avatar-size': `${size}px`,
          '--avatar-font-size': `${fontSize}px`,
        } as CSSProperties
      }
    >
      <div
        className={styles.face}
        style={emblem || showImage ? undefined : ({ '--face-bg': `var(${bgToken})` } as CSSProperties)}
      >
        {emblem ? (
          <div className={styles.emblem}>{emblem}</div>
        ) : showImage ? (
          <img src={src} alt="" className={styles.image} onError={() => setHasError(true)} />
        ) : (
          <span className={styles.initials} aria-hidden="true">
            {initials}
          </span>
        )}
      </div>

      {online && <div className={styles.onlineDot} aria-hidden="true" />}
    </div>
  );
};
